import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Tag,
  Button,
  Space,
  Select,
  Badge,
  Typography,
  Popconfirm,
  Card,
  Modal,
  Input,
  List,
  Alert,
  App,
} from 'antd';
import {
  UnorderedListOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ProfileOutlined,
  CloudDownloadOutlined,
  UserSwitchOutlined,
  EyeInvisibleOutlined,
  CodeOutlined,
  TeamOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api, User, isAdminRole, HttpError } from '../api';
import { t } from '../i18n';
import { QuotaBanner } from '../QuotaBanner';
import { DeviceConsole } from './DeviceConsole';
import { HintTooltip as Tooltip } from '../components/HintTooltip';
import { fmtDate, fmtDateTime } from '../time';

type DeviceRow = Awaited<ReturnType<typeof api.listDevices>>[number];
type FirmwareRelease = Awaited<ReturnType<typeof api.listFirmwareReleases>>[number];

function relTime(unix: number | null): string {
  if (!unix) return '—';
  // Clamp to 0: clock skew between browser and server can otherwise show a
  // fresh heartbeat as "-1s ago" for a moment, which reads as a bug.
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - unix);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function fmtTs(unix: number | null): string {
  if (!unix) return '—';
  return fmtDateTime(unix);
}

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  pending: 'gold',
  revoked: 'red',
};

/** Short label for a Last Error link cell — full text lives in the dialog. */
function truncateError(msg: string, max = 40): string {
  return msg.length > max ? `${msg.slice(0, max)}…` : msg;
}

/**
 * Verbose-logging control: enable remote debug logging for a fixed window, or
 * disable it. When active it shows the remaining minutes; the device refreshes
 * the countdown on every heartbeat. Admin-only, minimal single-control cell.
 */
function VerboseLogControl({
  device,
  onSet,
  loading,
}: {
  device: DeviceRow;
  onSet: (minutes: number) => void;
  loading: boolean;
}) {
  const now = Math.floor(Date.now() / 1000);
  const remainingMin = device.debugUntil > now ? Math.ceil((device.debugUntil - now) / 60) : 0;
  if (remainingMin > 0) {
    return (
      <Space size={4}>
        <Tag color="processing">{t('deviceVerboseActive', { n: remainingMin })}</Tag>
        <Button size="small" icon={<EyeInvisibleOutlined />} loading={loading} onClick={() => onSet(0)}>
          {t('deviceVerboseDisable')}
        </Button>
      </Space>
    );
  }
  return (
    <Tooltip title={t('deviceVerboseHint')}>
      <Select
        size="small"
        style={{ width: 130 }}
        popupMatchSelectWidth={false}
        placeholder={t('deviceVerboseEnable')}
        value={undefined}
        loading={loading}
        onChange={(m: number) => onSet(m)}
        options={[
          { value: 15, label: t('deviceVerboseFor', { n: 15 }) },
          { value: 30, label: t('deviceVerboseFor', { n: 30 }) },
          { value: 60, label: t('deviceVerboseFor', { n: 60 }) },
        ]}
      />
    </Tooltip>
  );
}

/** A "Disks" button gated on the device being online (live read only). It
 * navigates to the Disks workspace with the device pre-selected; the live
 * re-inventory happens there. */
function DisksButton({
  online,
  onClick,
}: {
  online: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={online ? t('deviceOpenDisksHint') : t('deviceOfflineRead')}>
      <Button
        size="small"
        icon={<UnorderedListOutlined />}
        disabled={!online}
        onClick={onClick}
      >
        {t('deviceOpenDisks')}
      </Button>
    </Tooltip>
  );
}

/** Combined Devices page: agents + their disk sessions in one place. */
export function Devices({ user }: { user: User }) {
  return isAdminRole(user.role) ? <AdminDevices isSuper={user.role === 'superadmin'} /> : <ClientDevices />;
}

type MyDevice = Awaited<ReturnType<typeof api.listMyDevices>>[number];

/** Client view: the same Devices table as admins (minus admin-only actions),
 * with names, live presence, and a live-read Disks button per device. */
function ClientDevices() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['my-devices'],
    queryFn: api.listMyDevices,
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });

  const columns: ColumnsType<MyDevice> = [
    {
      title: t('deviceLabel'),
      dataIndex: 'label',
      render: (label: string | null) => (
        <Typography.Text strong type={label ? undefined : 'secondary'}>
          {label || t('deviceUnnamed')}
        </Typography.Text>
      ),
    },
    { title: t('deviceStatus'), dataIndex: 'status', render: (s: string) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag> },
    {
      title: t('devicePresence'),
      dataIndex: 'online',
      render: (online: boolean) => <Badge status={online ? 'success' : 'default'} text={online ? t('deviceOnline') : t('deviceOffline')} />,
    },
    { title: t('deviceVersion'), dataIndex: 'currentVersion', render: (v: string | null) => v ?? '—' },
    { title: t('deviceLastSeen'), dataIndex: 'lastSeen', render: (ls: number | null) => <Tooltip title={fmtTs(ls)}><span>{relTime(ls)}</span></Tooltip> },
    {
      title: t('colActions'),
      key: 'actions',
      render: (_: unknown, d) =>
        d.status === 'active' ? (
          <DisksButton
            online={d.online}
            onClick={() => navigate(`/disks?device=${d.id}`)}
          />
        ) : null,
    },
  ];

  return (
    <div>
      <QuotaBanner role="client" />
      <Card title={t('devicesTitle')}>
        <Table<MyDevice>
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={q.data ?? []}
        loading={q.isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('emptyList') }}
      />
      </Card>
    </div>
  );
}

/**
 * Update button + version-picker/WhatsNew dialog next to a device's version.
 * UI + API only for now: the device pulls whatever is globally "latest" from
 * /api/device/v1/update/manifest on its own schedule — there is no
 * per-device server-triggered push yet — so this previews what's available
 * rather than actually sending anything.
 */
function DeviceUpdateControl({
  device,
  releases,
}: {
  device: DeviceRow;
  releases: FirmwareRelease[];
}) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const latest = releases[0];
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(latest?.version);

  const requestUpdate = useMutation({
    mutationFn: () => api.requestDeviceUpdate(device.id),
    onSuccess: () => {
      message.success(t('deviceUpdateRequested'));
      setOpen(false);
    },
    onError: (e) => message.error((e as Error).message),
  });

  // Offer an update ONLY when a release is STRICTLY NEWER than what the device
  // runs. The device enforces anti-rollback (it refuses a version ≤ current), so
  // proposing an older/equal one would just be a no-op that looks like an action.
  const cmpSemver = (a: string, b: string): number => {
    const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
    const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i++) {
      if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
    }
    return 0;
  };
  const cur = device.currentVersion ?? '0.0.0';
  if (!latest || cmpSemver(latest.version, cur) <= 0) return null;

  const currentIdx = releases.findIndex((r) => r.version === device.currentVersion);
  const selIdx = releases.findIndex((r) => r.version === selectedVersion);
  const whatsNew: FirmwareRelease[] =
    selIdx === -1
      ? []
      : currentIdx === -1 || selIdx >= currentIdx
        ? [releases[selIdx]!]
        : releases.slice(selIdx, currentIdx);

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Button
        size="small"
        icon={<CloudDownloadOutlined />}
        onClick={() => {
          setSelectedVersion(latest.version);
          setOpen(true);
        }}
      >
        {t('deviceUpdateBtn')}
      </Button>
      <Modal
        title={t('deviceUpdateDialogTitle', { label: device.label || device.id.substring(0, 8) })}
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setOpen(false)}>{t('dialogClose')}</Button>
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              loading={requestUpdate.isPending}
              onClick={() => requestUpdate.mutate()}
            >
              {t('deviceUpdateNowBtn')} → v{latest.version}
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Typography.Text type="secondary">{t('deviceUpdateCurrentVersion')}: </Typography.Text>
            <Typography.Text strong>{device.currentVersion ?? '—'}</Typography.Text>
          </div>
          <div>
            <Typography.Text type="secondary">{t('deviceUpdateSelectVersion')}</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={selectedVersion}
              onChange={setSelectedVersion}
              options={releases.map((r, i) => ({
                value: r.version,
                label: `${r.version}${i === 0 ? ` (${t('deviceUpdateLatestTag')})` : ''}${r.mandatory ? ` · ${t('deviceUpdateMandatoryTag')}` : ''}`,
              }))}
            />
          </div>
          <div>
            <Typography.Text strong>{t('deviceUpdateWhatsNew')}</Typography.Text>
            <List
              size="small"
              dataSource={whatsNew}
              locale={{ emptyText: t('deviceUpdateNoNotes') }}
              renderItem={(r) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Typography.Text code>{r.version}</Typography.Text>{' '}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {fmtDate(r.publishedAt)}
                    </Typography.Text>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{r.releaseNotes || t('deviceUpdateNoNotes')}</div>
                  </div>
                </List.Item>
              )}
            />
          </div>
          <Alert type="info" showIcon message={t('deviceUpdateNowHint')} />
        </Space>
      </Modal>
    </span>
  );
}

/** Dialog for a single device's last error: full text, timestamp, and a
 * trailing slice of recent log lines for context (closest thing to a trace
 * this C binary can offer — it has no stack unwinding). */
function DeviceErrorDialog({ device, onClose }: { device: DeviceRow; onClose: () => void }) {
  const logsQ = useQuery({
    queryKey: ['device-logs', device.id],
    queryFn: () => api.getDeviceLogs(device.id),
  });
  const recent = (logsQ.data?.items ?? []).slice(0, 10);

  return (
    <Modal
      title={t('deviceErrorDialogTitle', { label: device.label || device.id.substring(0, 8) })}
      open
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t('dialogClose')}</Button>}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div style={{ whiteSpace: 'pre-wrap' }}>{device.lastError}</div>
        <div>
          <Typography.Text type="secondary">{t('deviceErrorAt')}: </Typography.Text>
          <Typography.Text code>{fmtTs(device.lastErrorAt)}</Typography.Text>
        </div>
        <div>
          <Typography.Text strong>{t('deviceRecentLog')}</Typography.Text>
          <div style={{ maxHeight: 200, overflow: 'auto', marginTop: 4 }}>
            {recent.length === 0 ? (
              <Typography.Text type="secondary">{t('deviceNoLogs')}</Typography.Text>
            ) : (
              recent.map((l) => (
                <div key={l.id} style={{ fontSize: 12 }}>
                  <Typography.Text code>{fmtTs(l.ts)}</Typography.Text> — {l.message}
                </div>
              ))
            )}
          </div>
        </div>
      </Space>
    </Modal>
  );
}

/* Full log browsing moved to its own page (DeviceLogPage.tsx) — a modal was
 * too cramped to be useful, not even one full line fit. */

/**
 * Owner cell: shows the current assignment (or a warning if unassigned) and
 * a button that opens a small confirm dialog rather than committing on
 * every Select change — reassigning a device is consequential enough
 * (changes who can see its disks) to want a deliberate confirm step.
 */
function OwnerCell({
  device,
  ownerUserIds,
  owners,
  onSave,
}: {
  device: DeviceRow;
  ownerUserIds: string[];
  owners: Array<{ id: string; username: string; role: string; email?: string | null }>;
  onSave: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(ownerUserIds);
  // Admins are shown with a role suffix so they're distinguishable from clients.
  const labelOf = (u: { username: string; role: string }) =>
    u.role === 'client' ? u.username : `${u.username} · ${u.role}`;
  const nameOf = (id: string) => {
    const u = owners.find((x) => x.id === id);
    return u ? labelOf(u) : id;
  };

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Space direction="vertical" size={2}>
        {ownerUserIds.length > 0 ? (
          <Typography.Text>{ownerUserIds.map(nameOf).join(', ')}</Typography.Text>
        ) : (
          <Typography.Text type="warning">{t('deviceOwnerUnassigned')}</Typography.Text>
        )}
        <Button
          size="small"
          icon={<UserSwitchOutlined />}
          onClick={() => {
            setPending(ownerUserIds);
            setOpen(true);
          }}
        >
          {ownerUserIds.length > 0 ? t('deviceOwnerChange') : t('deviceOwnerAssign')}
        </Button>
      </Space>
      <Modal
        title={t('deviceOwnerDialogTitle', { label: device.label || device.id.substring(0, 8) })}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => {
          onSave(pending);
          setOpen(false);
        }}
        okText={t('deviceOwnerSave')}
      >
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder={t('deviceOwnerNone')}
          value={pending}
          onChange={setPending}
          optionFilterProp="label"
          options={owners.map((u) => ({ value: u.id, label: labelOf(u) }))}
        />
      </Modal>
    </span>
  );
}

function AdminDevices({ isSuper }: { isSuper: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [errorDialogFor, setErrorDialogFor] = useState<DeviceRow | null>(null);
  const [consoleFor, setConsoleFor] = useState<DeviceRow | null>(null);
  const [migrateFor, setMigrateFor] = useState<DeviceRow | null>(null);
  const [migrateNewId, setMigrateNewId] = useState('');
  // Poll on a short interval so presence (online/offline) and new connection
  // requests update in near-real-time without a manual page refresh.
  const q = useQuery({
    queryKey: ['devices'],
    queryFn: api.listDevices,
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: api.listUsers });
  // Assignable device owners: clients AND regular admins (superadmins see every
  // device anyway, so there's nothing to bind). Admins keep full visibility —
  // binding to an admin is a responsibility label, not an access restriction.
  const ownerUsers = (usersQ.data ?? []).filter((u) => u.role === 'client' || u.role === 'admin');
  const releasesQ = useQuery({ queryKey: ['firmware-releases'], queryFn: api.listFirmwareReleases });
  const releases = releasesQ.data ?? [];
  // Soft presence: who currently has each device's disk editor open.
  const presenceQ = useQuery({ queryKey: ['presence'], queryFn: api.presenceGet, refetchInterval: 10_000 });
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (e) {
        if (e instanceof HttpError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
  const othersViewing = (deviceId: string): string[] =>
    (presenceQ.data?.viewers[deviceId] ?? [])
      .filter((v) => v.userId !== meQ.data?.user.id)
      .map((v) => v.username);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['devices'] });
  const revoke = useMutation({ mutationFn: (id: string) => api.revokeDevice(id), onSuccess: invalidate });
  const reactivate = useMutation({ mutationFn: (id: string) => api.reactivateDevice(id), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteDevice(id), onSuccess: invalidate });
  const approve = useMutation({ mutationFn: (id: string) => api.approveDevice(id), onSuccess: invalidate });
  const migrate = useMutation({
    mutationFn: ({ id, newId }: { id: string; newId: string }) => api.migrateDeviceId(id, newId),
    onSuccess: (r) => {
      invalidate();
      setMigrateFor(null);
      setMigrateNewId('');
      message.success(t('deviceMigrateDone', { mode: r.mode, n: r.movedRows }));
    },
    onError: (e) => message.error((e as Error).message),
  });
  const deny = useMutation({ mutationFn: (id: string) => api.denyDevice(id), onSuccess: invalidate });
  const rename = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) => api.renameDevice(id, label),
    onSuccess: invalidate,
  });
  const setOwner = useMutation({
    mutationFn: ({ deviceId, ownerUserIds }: { deviceId: string; ownerUserIds: string[] }) =>
      api.setDeviceOwners(deviceId, ownerUserIds),
    onSuccess: invalidate,
  });
  const setDebug = useMutation({
    mutationFn: ({ id, minutes }: { id: string; minutes: number }) => api.setDeviceDebug(id, minutes),
    onSuccess: invalidate,
  });

  const rows = (q.data ?? [])
    .slice()
    // Stable order: pending (needs approval) first so new requests never get
    // lost, then alphabetical by name. Sorting by lastSeen made rows jump around
    // on every heartbeat.
    .sort((a, b) => {
      if (a.pending !== b.pending) return a.pending ? -1 : 1;
      return (a.label || a.id).localeCompare(b.label || b.id);
    });
  const pendingCount = (q.data ?? []).filter((d) => d.pending).length;

  const columns: ColumnsType<DeviceRow> = [
    {
      title: t('deviceLabel'),
      dataIndex: 'label',
      render: (_: unknown, d) => (
        <div>
          <Typography.Text
            strong
            editable={{
              tooltip: t('deviceRename'),
              text: d.label ?? '',
              onChange: (val) => {
                const next = val.trim();
                if (next !== (d.label ?? '')) rename.mutate({ id: d.id, label: next });
              },
            }}
            type={d.label ? undefined : 'secondary'}
          >
            {d.label || t('deviceUnnamed')}
          </Typography.Text>
          {othersViewing(d.id).length > 0 && (
            <Tag color="gold" style={{ fontSize: 11, marginTop: 2 }}>
              <TeamOutlined /> {t('presenceOpenBy', { users: othersViewing(d.id).join(', ') })}
            </Tag>
          )}
        </div>
      ),
    },
    { title: t('deviceFingerprint'), dataIndex: 'id', render: (id: string) => <Typography.Text code style={{ fontSize: 12 }}>{id}</Typography.Text> },
    {
      title: t('deviceStatus'),
      dataIndex: 'status',
      render: (s: string, d) => (
        <Space direction="vertical" size={2}>
          {/* "active" is the expected default for anything showing up in this
           * list at all — only flag the states that actually need attention. */}
          {s !== 'active' && <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag>}
          <Tag color={d.online ? 'green' : 'default'}>{d.online ? t('deviceOnline') : t('deviceOffline')}</Tag>
        </Space>
      ),
    },
    {
      title: t('deviceVersion'),
      dataIndex: 'currentVersion',
      render: (v: string | null, d) => (
        <Space size={6} onClick={(e) => e.stopPropagation()}>
          <span>{v ?? '—'}</span>
          <DeviceUpdateControl device={d} releases={releases} />
        </Space>
      ),
    },
    {
      title: t('deviceOwner'),
      dataIndex: 'ownerUserIds',
      render: (ownerUserIds: string[], d) => (
        <OwnerCell
          device={d}
          ownerUserIds={ownerUserIds ?? []}
          owners={ownerUsers}
          onSave={(next) => setOwner.mutate({ deviceId: d.id, ownerUserIds: next })}
        />
      ),
    },
    {
      title: t('deviceLastSeen'),
      dataIndex: 'lastSeen',
      render: (ls: number | null, d) => (
        <div>
          <Tooltip title={fmtTs(ls)}><span>{relTime(ls)}</span></Tooltip>
          {d.lastSeenIp && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }} code>
                {d.lastSeenIp}
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('colActions'),
      key: 'actions',
      width: 280,
      // Lay the row actions out in a 2-column grid so the column stays compact
      // without becoming a tall vertical strip (one button per row) that eats the
      // whole row height on devices with many available actions.
      render: (_: unknown, d) => (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, alignItems: 'start' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button size="small" icon={<ProfileOutlined />} onClick={() => navigate(`/devices/${d.id}/log`)}>
            {t('deviceShowLog')}
          </Button>
          {d.status === 'active' && (
            <VerboseLogControl
              device={d}
              loading={setDebug.isPending && setDebug.variables?.id === d.id}
              onSet={(minutes) => setDebug.mutate({ id: d.id, minutes })}
            />
          )}
          {isSuper && d.status === 'active' && (
            <Button size="small" icon={<CodeOutlined />} onClick={() => setConsoleFor(d)}>
              {t('deviceConsole')}
            </Button>
          )}
          {d.status === 'active' && (
            <DisksButton
              online={d.online}
              onClick={() => navigate(`/disks?device=${d.id}`)}
            />
          )}
          {d.status === 'pending' && (
            <>
              <Button type="primary" size="small" icon={<CheckOutlined />} loading={approve.isPending} onClick={() => approve.mutate(d.id)}>
                {t('deviceApprove')}
              </Button>
              <Popconfirm title={t('deviceDenyConfirm')} onConfirm={() => deny.mutate(d.id)}>
                <Button danger size="small" icon={<CloseOutlined />}>{t('deviceDeny')}</Button>
              </Popconfirm>
            </>
          )}
          {d.status === 'active' && (
            <Popconfirm title={t('deviceRevokeConfirm')} onConfirm={() => revoke.mutate(d.id)}>
              <Button danger size="small" icon={<StopOutlined />}>{t('deviceRevokeBtn')}</Button>
            </Popconfirm>
          )}
          {d.status === 'revoked' && (
            <>
              <Popconfirm title={t('deviceReactivateConfirm')} onConfirm={() => reactivate.mutate(d.id)}>
                <Button size="small" icon={<ReloadOutlined />}>{t('deviceReactivateBtn')}</Button>
              </Popconfirm>
              <Popconfirm title={t('deviceDeleteConfirm')} onConfirm={() => remove.mutate(d.id)}>
                <Button danger size="small" icon={<DeleteOutlined />} loading={remove.isPending}>{t('deviceDeleteBtn')}</Button>
              </Popconfirm>
            </>
          )}
          {isSuper && (
            <Tooltip title={t('deviceMigrateIdHint')}>
              <Button
                size="small"
                icon={<SwapOutlined />}
                onClick={() => {
                  setMigrateFor(d);
                  setMigrateNewId('');
                }}
              >
                {t('deviceMigrateIdBtn')}
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            {t('devicesTitle')}
            {pendingCount > 0 && <Tag color="gold">{t('devicePendingCount', { n: pendingCount })}</Tag>}
          </Space>
        }
      >
        <Table<DeviceRow>
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={q.isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          onRow={(d) =>
            d.ownerUserIds && d.ownerUserIds.length > 0
              ? {}
              : { style: { background: 'rgba(250, 173, 20, 0.08)' } }
          }
        />
      </Card>
      {errorDialogFor && <DeviceErrorDialog device={errorDialogFor} onClose={() => setErrorDialogFor(null)} />}
      {consoleFor && (
        <DeviceConsole
          deviceId={consoleFor.id}
          deviceName={consoleFor.label || consoleFor.id.substring(0, 8)}
          open={!!consoleFor}
          onClose={() => setConsoleFor(null)}
        />
      )}
      <Modal
        title={t('deviceMigrateIdTitle')}
        open={!!migrateFor}
        onCancel={() => setMigrateFor(null)}
        onOk={() => migrateFor && migrate.mutate({ id: migrateFor.id, newId: migrateNewId.trim() })}
        okText={t('deviceMigrateIdOk')}
        okButtonProps={{ disabled: !migrateNewId.trim(), loading: migrate.isPending }}
        confirmLoading={migrate.isPending}
      >
        {migrateFor && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 4 }}>
              {t('deviceMigrateIdBody')}
            </Typography.Paragraph>
            <Typography.Text type="secondary">{t('deviceMigrateIdCurrent')}</Typography.Text>
            <Typography.Text code copyable>
              {migrateFor.id}
            </Typography.Text>
            <Input
              placeholder="hwid1:…"
              value={migrateNewId}
              onChange={(e) => setMigrateNewId(e.target.value)}
              onPressEnter={() =>
                migrateNewId.trim() && migrate.mutate({ id: migrateFor.id, newId: migrateNewId.trim() })
              }
            />
          </Space>
        )}
      </Modal>
    </div>
  );
}
